import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", username: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center p-6">
      <div className="w-full max-w-md swiss-card p-8" data-testid="register-card">
        {done ? (
          <div className="text-center" data-testid="register-success">
            <div className="status-abgeschlossen inline-flex px-3 py-1 text-xs font-medium mb-4" style={{ borderRadius: 4 }}>
              ✓ {t("common.saved")}
            </div>
            <h1 className="text-2xl font-bold tracking-tight">{t("register.title")}</h1>
            <p className="text-sm text-muted-foreground mt-3">{t("register.pendingMsg")}</p>
            <Button
              onClick={() => navigate("/login")}
              className="mt-6 bg-[#0055FF] hover:bg-[#0044CC] text-white"
              style={{ borderRadius: 4 }}
              data-testid="register-back-to-login"
            >
              {t("login.submit")}
            </Button>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold tracking-tight">{t("register.title")}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t("register.subtitle")}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Field label={t("register.name")}>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="register-name" style={{ borderRadius: 4 }} />
              </Field>
              <Field label={t("register.email")}>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="register-email" style={{ borderRadius: 4 }} />
              </Field>
              <Field label={t("register.username")}>
                <Input value={form.username} onChange={(e) => set("username", e.target.value)} required data-testid="register-username" style={{ borderRadius: 4 }} />
              </Field>
              <Field label={t("register.password")}>
                <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} data-testid="register-password" style={{ borderRadius: 4 }} />
              </Field>
              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white"
                style={{ borderRadius: 4 }}
                data-testid="register-submit-button"
              >
                {busy ? t("common.loading") : t("register.submit")}
              </Button>
            </form>
            <p className="text-sm text-muted-foreground mt-6 text-center">
              {t("register.haveAccount")}{" "}
              <Link to="/login" className="text-[#0055FF] font-medium hover:underline" data-testid="register-login-link">
                {t("register.loginLink")}
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-mini mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
